import { useState, type FormEvent, type ReactNode } from "react";
import { ArrowLeftRight, BedDouble, Minus, Plane, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { FLIGHT_CITIES } from "../data/mock-flights";
import { MOCK_HOTELS } from "../data/mock-hotels";
import {
  useMarketplaceSearch,
  type TravelProduct,
} from "../context/SearchContext";

const stayCities = Array.from(new Set(MOCK_HOTELS.map((hotel) => hotel.city)));

type Props = {
  onNavy?: boolean;
  onSearch: (product: TravelProduct) => void;
};

export function SearchPanel({ onNavy = false, onSearch }: Props) {
  const { search, setSearch } = useMarketplaceSearch();
  const [error, setError] = useState("");
  const [whoOpen, setWhoOpen] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (search.product === "stays" && search.checkOut <= search.checkIn) {
      setError("Check-out has to be after check-in.");
      return;
    }
    if (
      search.product === "flights" &&
      search.tripType === "round" &&
      search.returnDate < search.departDate
    ) {
      setError("Return has to be on or after the departure date.");
      return;
    }
    setError("");
    setWhoOpen(false);
    onSearch(search.product);
  };

  const tabClass = (active: boolean) =>
    cn(
      "inline-flex items-center gap-2 rounded-t-md px-4 py-2 text-sm font-semibold",
      active
        ? "bg-white text-[#003580]"
        : onNavy
          ? "text-white hover:bg-white/10"
          : "text-[#1a1a1a] hover:bg-white/70",
    );

  return (
    <form onSubmit={submit}>
      <div className="flex gap-1">
        <button
          type="button"
          className={tabClass(search.product === "stays")}
          aria-pressed={search.product === "stays"}
          onClick={() => setSearch({ product: "stays" })}
        >
          <BedDouble className="h-4 w-4" />
          Stays
        </button>
        <button
          type="button"
          className={tabClass(search.product === "flights")}
          aria-pressed={search.product === "flights"}
          onClick={() => setSearch({ product: "flights" })}
        >
          <Plane className="h-4 w-4" />
          Flights
        </button>
      </div>

      <div className="rounded-lg bg-white p-2 text-[#1a1a1a] shadow-xl ring-1 ring-black/5">
        {search.product === "stays" ? (
          <div className="grid gap-1 md:grid-cols-[1.4fr_1fr_1fr_1fr_auto] md:divide-x md:divide-[#e7e7e7]">
            <Field label="Where to">
              <input
                list="stay-cities"
                value={search.destination}
                onChange={(event) =>
                  setSearch({ destination: event.target.value })
                }
                placeholder="City or hotel"
                className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                aria-label="Destination"
              />
              <datalist id="stay-cities">
                {stayCities.map((city) => (
                  <option key={city} value={city} />
                ))}
              </datalist>
            </Field>
            <Field label="Check-in">
              <input
                type="date"
                value={search.checkIn}
                onChange={(event) => setSearch({ checkIn: event.target.value })}
                className="w-full bg-transparent text-sm outline-none"
                aria-label="Check-in"
              />
            </Field>
            <Field label="Check-out">
              <input
                type="date"
                value={search.checkOut}
                min={search.checkIn}
                onChange={(event) => setSearch({ checkOut: event.target.value })}
                className="w-full bg-transparent text-sm outline-none"
                aria-label="Check-out"
              />
            </Field>
            <div className="relative">
              <button
                type="button"
                className="h-full w-full px-3 py-2 text-left"
                onClick={() => setWhoOpen((open) => !open)}
                aria-expanded={whoOpen}
              >
                <span className="block text-[11px] font-semibold">Guests</span>
                <span className="text-sm">
                  {search.guests} adult{search.guests === 1 ? "" : "s"} ·{" "}
                  {search.rooms} room{search.rooms === 1 ? "" : "s"}
                </span>
              </button>
              {whoOpen ? (
                <div className="absolute right-0 z-20 mt-1 w-64 rounded-lg border border-[#e7e7e7] bg-white p-3 shadow-lg">
                  <Stepper
                    label="Adults"
                    value={search.guests}
                    min={1}
                    max={8}
                    onChange={(guests) => setSearch({ guests })}
                  />
                  <Stepper
                    label="Rooms"
                    value={search.rooms}
                    min={1}
                    max={4}
                    onChange={(rooms) => setSearch({ rooms })}
                  />
                  <Button
                    type="button"
                    className="mt-3 h-9 w-full"
                    onClick={() => setWhoOpen(false)}
                  >
                    Done
                  </Button>
                </div>
              ) : null}
            </div>
            <div className="flex items-stretch p-1">
              <Button type="submit" className="h-12 w-full px-6 md:w-auto">
                Search
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2 px-2 pt-1 text-sm">
              <Choice
                pressed={search.tripType === "round"}
                onClick={() => setSearch({ tripType: "round" })}
              >
                Round trip
              </Choice>
              <Choice
                pressed={search.tripType === "oneway"}
                onClick={() => setSearch({ tripType: "oneway" })}
              >
                One way
              </Choice>
              <select
                value={search.cabin}
                onChange={(event) =>
                  setSearch({
                    cabin: event.target.value as "Economy" | "Business",
                  })
                }
                className="rounded-md border border-[#e7e7e7] bg-white px-2 py-1 text-sm md:ml-auto"
                aria-label="Cabin"
              >
                <option>Economy</option>
                <option>Business</option>
              </select>
            </div>
            <div
              className={cn(
                "grid gap-1 md:divide-x md:divide-[#e7e7e7]",
                search.tripType === "round"
                  ? "md:grid-cols-[1fr_auto_1fr_1fr_1fr_1fr_auto]"
                  : "md:grid-cols-[1fr_auto_1fr_1fr_1fr_auto]",
              )}
            >
              <Field label="From">
                <input
                  list="flight-cities"
                  value={search.origin}
                  onChange={(event) => setSearch({ origin: event.target.value })}
                  placeholder="Mumbai"
                  className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                  aria-label="Flying from"
                />
              </Field>
              <div className="flex items-center justify-center">
                <button
                  type="button"
                  className="rounded-full border border-[#e7e7e7] p-2 text-[#003580]"
                  aria-label="Swap cities"
                  onClick={() =>
                    setSearch({
                      origin: search.flightTo,
                      flightTo: search.origin,
                    })
                  }
                >
                  <ArrowLeftRight className="h-4 w-4" />
                </button>
              </div>
              <Field label="To">
                <input
                  list="flight-cities"
                  value={search.flightTo}
                  onChange={(event) =>
                    setSearch({ flightTo: event.target.value })
                  }
                  placeholder="Goa"
                  className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                  aria-label="Flying to"
                />
                <datalist id="flight-cities">
                  {FLIGHT_CITIES.map((city) => (
                    <option key={city} value={city} />
                  ))}
                </datalist>
              </Field>
              <Field label="Depart">
                <input
                  type="date"
                  value={search.departDate}
                  onChange={(event) =>
                    setSearch({ departDate: event.target.value })
                  }
                  className="w-full bg-transparent text-sm outline-none"
                  aria-label="Departure date"
                />
              </Field>
              {search.tripType === "round" ? (
                <Field label="Return">
                  <input
                    type="date"
                    value={search.returnDate}
                    min={search.departDate}
                    onChange={(event) =>
                      setSearch({ returnDate: event.target.value })
                    }
                    className="w-full bg-transparent text-sm outline-none"
                    aria-label="Return date"
                  />
                </Field>
              ) : null}
              <div className="relative">
                <button
                  type="button"
                  className="h-full w-full px-3 py-2 text-left"
                  onClick={() => setWhoOpen((open) => !open)}
                >
                  <span className="block text-[11px] font-semibold">
                    Travellers
                  </span>
                  <span className="text-sm">
                    {search.passengers} passenger
                    {search.passengers === 1 ? "" : "s"}
                  </span>
                </button>
                {whoOpen ? (
                  <div className="absolute right-0 z-20 mt-1 w-64 rounded-lg border border-[#e7e7e7] bg-white p-3 shadow-lg">
                    <Stepper
                      label="Passengers"
                      value={search.passengers}
                      min={1}
                      max={6}
                      onChange={(passengers) => setSearch({ passengers })}
                    />
                    <Button
                      type="button"
                      className="mt-3 h-9 w-full"
                      onClick={() => setWhoOpen(false)}
                    >
                      Done
                    </Button>
                  </div>
                ) : null}
              </div>
              <div className="flex items-stretch p-1">
                <Button type="submit" className="h-12 w-full px-6 md:w-auto">
                  Search
                </Button>
              </div>
            </div>
          </div>
        )}
        {error ? (
          <p className="px-3 pb-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block min-w-0 px-3 py-2">
      <span className="block text-[11px] font-semibold">{label}</span>
      {children}
    </label>
  );
}

function Choice({
  pressed,
  onClick,
  children,
}: {
  pressed: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        "rounded-full px-3 py-1",
        pressed ? "bg-[#003580] text-white" : "text-[#1a1a1a] hover:bg-slate-100",
      )}
    >
      {children}
    </button>
  );
}

function Stepper({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="rounded-full border border-[#e7e7e7] p-1 disabled:opacity-40"
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          aria-label={`Fewer ${label}`}
        >
          <Minus className="h-4 w-4" />
        </button>
        <span className="w-4 text-center text-sm">{value}</span>
        <button
          type="button"
          className="rounded-full border border-[#e7e7e7] p-1 disabled:opacity-40"
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
          aria-label={`More ${label}`}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
